'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { createClient } from '@/lib/supabase/client';

interface FormData {
  store_name: string;
  owner_name: string;
  owner_email: string;
  phone: string;
  city: string;
  business_type: string;
  message: string;
  store_address: string;
}

const BUSINESS_TYPES = [
  'Boutique / Commerce général',
  'Alimentation / Épicerie',
  'Pharmacie / Parapharmacie',
  'Électronique / Téléphonie',
  'Vêtements / Mode',
  'Matériaux de construction',
  'Restaurant / Restauration',
  'Autre',
];

export default function RegisterStorePage() {
  const [form, setForm] = useState<FormData>({
    store_name: '',
    owner_name: '',
    owner_email: '',
    phone: '',
    city: '',
    business_type: '',
    message: '',
    store_address: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase
        .from('prospect_registrations')
        .insert([{ ...form, status: 'pending' }]);
      if (dbError) throw dbError;

      // Notify super admin via email (fire-and-forget — don't block success)
      try {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
        await fetch(`${supabaseUrl}/functions/v1/notify-prospect-registration`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseAnonKey ?? '',
            'Authorization': `Bearer ${supabaseAnonKey ?? ''}`,
          },
          body: JSON.stringify(form),
        });
      } catch {
        // Email failure is non-blocking — registration still succeeds
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err?.message ?? 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div suppressHydrationWarning className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-[#1B4FD8]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-[#1B4FD8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-[#0F172A] mb-3">Demande envoyée !</h2>
          <p className="text-[#64748B] mb-2 leading-relaxed">
            Votre demande de création de boutique a bien été reçue. Notre équipe va l'examiner et vous contactera sous peu pour finaliser la configuration.
          </p>
          <p className="text-sm text-[#94A3B8] mb-8">
            Un email de confirmation vous sera envoyé à <span className="font-medium text-[#1B4FD8]">{form.owner_email}</span>
          </p>
          <Link
            href="/presentation"
            className="inline-flex items-center gap-2 bg-[#1B4FD8] text-white font-semibold px-6 py-3 rounded-xl hover:bg-[#1640b0] transition-colors"
          >
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div suppressHydrationWarning className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <nav className="bg-white border-b border-[#E2E8F0] px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link href="/presentation" className="flex items-center gap-2">
            <Image
              src="/assets/images/app_logo.png"
              alt="Easy Frame logo"
              width={32}
              height={32}
              className="rounded-lg"
            />
            <span className="font-bold text-lg text-[#0F172A]">Easy Frame</span>
          </Link>
          <Link href="/presentation" className="text-sm text-[#64748B] hover:text-[#1B4FD8] transition-colors flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Retour
          </Link>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 bg-[#EFF6FF] text-[#1B4FD8] text-xs font-semibold px-3 py-1.5 rounded-full mb-4 border border-[#1B4FD8]/20">
            <span className="w-1.5 h-1.5 rounded-full bg-[#1B4FD8] animate-pulse" />
            Création de boutique
          </span>
          <h1 className="text-3xl font-extrabold text-[#0F172A] mb-3">Créez votre boutique</h1>
          <p className="text-[#64748B] leading-relaxed">
            Renseignez vos informations ci-dessous. Notre équipe finalisera la configuration de votre boutique et vous contactera rapidement.
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-8">
          {error && (
            <div className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Store name */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Nom de la boutique <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="store_name"
                  value={form.store_name}
                  onChange={handleChange}
                  required
                  placeholder="Ex: Boutique Mama Bénédicte"
                  className="w-full px-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] placeholder:text-[#94A3B8]"
                />
              </div>

              {/* Store address */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Adresse de la boutique
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-[#94A3B8]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="store_address"
                    value={form.store_address}
                    onChange={handleChange}
                    placeholder="Ex: Avenue du Commerce, N°12, Gombe, Kinshasa"
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] placeholder:text-[#94A3B8]"
                  />
                </div>
              </div>

              {/* Owner name */}
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Votre nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="owner_name"
                  value={form.owner_name}
                  onChange={handleChange}
                  required
                  placeholder="Prénom et nom"
                  className="w-full px-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] placeholder:text-[#94A3B8]"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Adresse email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="owner_email"
                  value={form.owner_email}
                  onChange={handleChange}
                  required
                  placeholder="vous@exemple.com"
                  className="w-full px-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] placeholder:text-[#94A3B8]"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+243 8XX XXX XXX"
                  className="w-full px-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] placeholder:text-[#94A3B8]"
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Ville
                </label>
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="Ex: Kinshasa, Lubumbashi..."
                  className="w-full px-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] placeholder:text-[#94A3B8]"
                />
              </div>

              {/* Business type */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Type de commerce
                </label>
                <select
                  name="business_type"
                  value={form.business_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] bg-white"
                >
                  <option value="">Sélectionnez votre type de commerce</option>
                  {BUSINESS_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-[#0F172A] mb-1.5">
                  Message (optionnel)
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Décrivez brièvement votre activité ou vos besoins spécifiques..."
                  className="w-full px-4 py-2.5 text-sm border border-[#E2E8F0] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1B4FD8]/30 focus:border-[#1B4FD8] text-[#0F172A] placeholder:text-[#94A3B8] resize-none"
                />
              </div>
            </div>

            {/* Info notice */}
            <div className="flex items-start gap-3 bg-[#EFF6FF] border border-[#1B4FD8]/20 rounded-xl p-4">
              <svg className="w-5 h-5 text-[#1B4FD8] shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
              </svg>
              <p className="text-xs text-[#1B4FD8] leading-relaxed">
                Après soumission, notre administrateur examinera votre demande, choisira la formule adaptée et finalisera la création de votre boutique. Vous serez contacté par email.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#1B4FD8] text-white font-semibold rounded-xl hover:bg-[#1640b0] transition-colors disabled:opacity-60 flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                  Envoyer ma demande
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
